// Donation Forms landing page — the behaviours Bootstrap can't do declaratively.
//
// Most of the hero preview is stock Bootstrap markup: the Guided/Express switch
// is nav-pill tabs, and the four-step walkthrough is a carousel. What's here is
// the brand-colour swatches, plus starting and pausing that carousel's autoplay.
//
// Autoplay is opt-in from JS rather than declared with `data-bs-ride="carousel"`
// in the markup, and that inversion is deliberate — see below.

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'
const STEP_INTERVAL = 3000

const onReady = (fn) =>
  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', fn)
    : fn()

// Bootstrap disables carousel *transitions* under reduced motion but keeps
// cycling, so the autoplay has to be handled here. Calling pause() is not enough
// and neither is clearing the interval: `_maybeEnableCycle()` restarts cycling
// whenever `_config.ride` is truthy, and Bootstrap calls it from three places —
// mouseleave, a timeout after touchend, and the click data-api after every
// prev/next/indicator activation. With `data-bs-ride` in the markup, one hover,
// one swipe or one click on a control brought the autoplay back for the rest of
// the visit.
//
// So the markup ships no `data-bs-ride` and this only enables cycling when motion
// is welcome. Under reduced motion we create no instance at all: the click
// data-api lazily makes one on first control use, with Bootstrap's default
// `ride: false`, so cycling can't start by any of those three routes. Nothing to
// re-disable, which is the point — a guard that can't be undone beats one that
// has to win a race with three callers.
//
// Note `interval: false` is NOT used to express "don't cycle". _updateInterval()
// would copy it into _config.interval, and setInterval(fn, false) coerces to 0 —
// a runaway loop rather than a stopped one. `ride` is the only correct lever.
const startAutoplay = (carousel) => {
  const Carousel = window.bootstrap?.Carousel
  if (!Carousel) return null

  return Carousel.getOrCreateInstance(carousel, {
    ride: 'carousel',
    interval: STEP_INTERVAL,
  })
}

// Bootstrap's `pause: 'hover'` wires mouseenter/mouseleave only — there are no
// focus handlers in carousel.js. Without these, a keyboard user tabbing to the
// prev/next buttons keeps getting the slide pulled out from under them every
// three seconds. Pause while focus is anywhere inside, resume on the way out.
const pauseWhileFocused = (carousel, instance) => {
  carousel.addEventListener('focusin', () => instance.pause())

  carousel.addEventListener('focusout', (event) => {
    if (carousel.contains(event.relatedTarget)) return

    // Don't resume if the pointer is still resting here: Bootstrap's own hover
    // pause only re-arms on the next mouseenter, so cycling would restart under a
    // stationary cursor and stay running.
    if (!carousel.matches(':hover')) instance.cycle()
  })
}

onReady(() => {
  const preview = document.querySelector('[data-df-preview]')
  if (!preview) return

  // ── brand colour swatches ──
  const swatches = [...preview.querySelectorAll('[data-df-brand-color]')]

  swatches.forEach((swatch) => {
    swatch.addEventListener('click', () => {
      // Set on the preview root rather than each mock: both the Guided and
      // Express panes read --df-brand, so one write re-skins whichever is
      // visible and the other for when it's switched to.
      preview.style.setProperty('--df-brand', swatch.dataset.dfBrandColor)
      swatches.forEach((s) => s.setAttribute('aria-pressed', String(s === swatch)))
    })
  })

  // ── autoplay, only where it's wanted ──
  if (window.matchMedia(REDUCED_MOTION).matches) return

  preview.querySelectorAll('.carousel').forEach((carousel) => {
    const instance = startAutoplay(carousel)
    if (instance) pauseWhileFocused(carousel, instance)
  })
})
